/**
 * Filters sheet — INVENTORY §2 row 1 / §4.9; prototype template lines 795-890.
 * Shared by Discover, Tonight, Interests, GOTY and Playtests; App mounts it
 * whenever `s.fltOpen` is true and the sheet closes itself.
 *
 * ONE SHEET, ONE SHAPE, EVERY SCREEN (2026-08-27, Marcus) — desktop's
 * `FilterPanel` carries the same note and the same sections in the same order.
 * The `# TAGS` picker that was its own sheet (`TagSheet.tsx`, deleted) is the
 * last section here, and nothing is hidden by host any more: `passFlt` and
 * `selectors.ts#tagLensOf` make every filter, tags included, actually narrow on
 * every screen instead of being offered and ignored — over state each screen
 * OWNS (`store/filterKey.ts`), so nothing set here follows you anywhere else.
 *
 * The dual-thumb price control is two stacked `input[type=range]` inside the
 * frozen `.gtm-dual` box (`styles/app.css`): the tracks are transparent and
 * `pointer-events:none`, so only the thumbs take the pointer and the two inputs
 * can overlap. Snapping (±2 → multiple of 10) lives in `lib/logic.ts#snapPrice`
 * and is applied by `setPrLo` / `setPrHi`, not here.
 */

import { useRef, useState } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { Sheet, SHEET_CHROME_Z, SHEET_HEADER_H } from '../components/Sheet.tsx';
import type { Filters, TriState } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { filtersVM } from '../store/selectors.ts';
import type { TagChip } from '../store/selectors.ts';

const LABEL = {
  fontFamily: 'var(--gt-font-pixel)',
  fontSize: 8,
  color: 'var(--acc)',
} as const;

const MONO = 'var(--gt-font-mono)';

/** The five tick marks sit at 1/6 increments of the usable track. */
const TICKS = [0.16667, 0.33333, 0.5, 0.66667, 0.83333];

export default function FiltersSheet(): JSX.Element {
  const s = useQd();
  const v = filtersVM(s);
  /** The ALL STEAM TAGS block — see the comment at its call site. */
  const tagSearchRef = useRef<HTMLDivElement>(null);
  const [tagFocus, setTagFocus] = useState(false);

  /** A tag chip cycles off → include → exclude → off; the sheet never removes. */
  const tagChip = (t: TagChip) => (
    <div
      key={t.key}
      onClick={() => s.cycleTag(t.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        border: '1px solid ' + t.bd,
        color: t.clr,
        background: t.bg,
        padding: '10px 11px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontFamily: MONO,
        fontSize: 10,
      }}
    >
      {t.pre}
      {t.l}
    </div>
  );

  const bubble = (pct: number, txt: string) => (
    <div
      style={{
        position: 'absolute',
        top: -22,
        left: 'calc(13px + (100% - 26px) * ' + pct + ' / 100)',
        transform: 'translateX(-50%)',
        background: 'var(--gt-card)',
        border: '1px solid var(--acc)',
        color: 'var(--acc)',
        fontFamily: 'var(--gt-font-mono)',
        fontSize: 10,
        padding: '2px 5px',
        whiteSpace: 'nowrap',
        zIndex: 6,
      }}
    >
      {txt}
    </div>
  );

  return (
    <Sheet
      label="Filters sheet"
      /*
        FULL SCREEN, on Marcus's ask (2026-09-02): *"I still want the modal to
        take up the full screen"*. The prototype's 86 was never really 86 on a
        phone — `vh` resolves against the large viewport, so with a URL bar
        showing it landed near 97% and read as full-height, which is the one he
        had been using. Now that the cap resolves against what is actually on
        screen (see `Sheet`), 86 would be a visible 14% strip of board above the
        sheet — a change he did not ask for and does not want. 100 states the
        behaviour he already had, and states it honestly.
      */
      maxHeight="100vh"
      onClose={() => s.toggleFltOpen(false)}
      header={{ title: '+ FILTERS', note: v.matchTxt }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          // SCROLL ROOM, and only while the tag field has focus. Measured at
          // 412x915: the ALL STEAM TAGS block is the LAST thing in the sheet, so
          // `scrollIntoView({ block: 'start' })` ran the panel to its scroll
          // limit and left the field 666px down a 786px panel — there was simply
          // nothing below it to scroll against, and shrinking the panel for the
          // keyboard does not create any (it removes viewport and content in
          // step). This padding is what the section scrolls up over. It is below
          // the fold and under the keyboard the whole time it exists, and it is
          // gone again on blur.
          padding: tagFocus ? '14px 14px 70vh' : '14px 14px 18px',
          fontFamily: 'var(--gt-font-mono)',
          fontSize: 11,
        }}
      >
        {/* PLAYERS ------------------------------------------------------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={LABEL}>PLAYERS</div>
          <div style={{ display: 'flex' }}>
            {v.players.map((c) => (
              <div
                key={c.key}
                onClick={() => s.setFlt({ p: c.v })}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid ' + c.bd,
                  color: c.clr,
                  background: c.bg,
                  height: 42,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  marginLeft: -1,
                  position: 'relative',
                  zIndex: Number(c.zi),
                  boxSizing: 'border-box',
                  fontSize: 10.5,
                }}
              >
                {c.l}
              </div>
            ))}
          </div>
        </div>

        {/* PRICE — no host gate: `passFlt` applies it on every screen now. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div style={LABEL}>PRICE</div>
            <div style={{ flex: 1 }} />
            <span style={{ color: 'var(--acc)', fontSize: 10.5 }}>{v.prLabel}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: 'var(--gt-faint)', fontSize: 10 }}>$0</span>
            <div className="gtm-dual">
              <div style={{ position: 'absolute', left: 0, right: 0, top: 21, height: 2, background: 'var(--gt-border-2)' }} />
              {TICKS.map((t) => (
                <div
                  key={t}
                  style={{
                    position: 'absolute',
                    left: 'calc(13px + (100% - 26px) * ' + t + ' - 1px)',
                    top: 12,
                    width: 2,
                    height: 20,
                    background: 'var(--gt-tick)',
                  }}
                />
              ))}
              <div
                style={{
                  position: 'absolute',
                  top: 21,
                  height: 2,
                  background: 'var(--acc)',
                  left: 'calc(13px + (100% - 26px) * ' + v.prLoPct + ' / 100)',
                  width: 'calc((100% - 26px) * ' + v.prWPct + ' / 100)',
                }}
              />
              {v.prLoBub ? bubble(v.prLoPct, v.prLoTxt) : null}
              {v.prHiBub ? bubble(v.prHiPct, v.prHiTxt) : null}
              <input
                type="range"
                min={0}
                max={60}
                step={1}
                value={v.prLo}
                onChange={(e) => s.setPrLo(Number(e.target.value))}
                onPointerDown={() => s.setFltPrDrag('lo')}
                onPointerUp={() => s.setFltPrDrag('')}
                style={{ zIndex: 3 }}
              />
              <input
                type="range"
                min={0}
                max={60}
                step={1}
                value={v.prHi}
                onChange={(e) => s.setPrHi(Number(e.target.value))}
                onPointerDown={() => s.setFltPrDrag('hi')}
                onPointerUp={() => s.setFltPrDrag('')}
                style={{ zIndex: 4 }}
              />
            </div>
            <span style={{ color: 'var(--gt-faint)', fontSize: 10 }}>$60+</span>
          </div>
        </div>

        {/* DISCOUNT ------------------------------------------------------ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={LABEL}>DISCOUNT</div>
          <div style={{ display: 'flex' }}>
            {v.disc.map((c) => (
              <div
                key={c.key}
                onClick={() => s.setFlt({ disc: c.v })}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid ' + c.bd,
                  color: c.clr,
                  background: c.bg,
                  height: 42,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  marginLeft: -1,
                  position: 'relative',
                  zIndex: Number(c.zi),
                  boxSizing: 'border-box',
                  fontSize: 10.5,
                }}
              >
                {c.l}
              </div>
            ))}
          </div>
        </div>

        {/*
          REVIEWS — Steam's own rung as a floor. No minimum-review-COUNT row
          beside it on purpose: Steam folds the count into the rung, so
          VERY POS+ already means 50+ reviews and OVERWHELMING means 500+.
        */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={LABEL}>REVIEWS</div>
          <div style={{ display: 'flex' }}>
            {v.rev.map((c) => (
              <div
                key={c.key}
                onClick={() => s.setFlt({ rs: c.v })}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  border: '1px solid ' + c.bd,
                  color: c.clr,
                  background: c.bg,
                  height: 42,
                  cursor: 'pointer',
                  marginLeft: -1,
                  position: 'relative',
                  zIndex: Number(c.zi),
                  boxSizing: 'border-box',
                  // Smaller than its siblings and allowed to wrap: five Steam
                  // rung names do not fit a phone row at 10.5px, and clipping
                  // MOSTLY POS+ to MOSTLY would make two options read alike.
                  fontSize: 8.5,
                  lineHeight: 1.25,
                  padding: '0 3px',
                }}
              >
                {c.l}
              </div>
            ))}
          </div>
        </div>

        {/* STATUS -------------------------------------------------------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <div style={LABEL}>STATUS</div>
          {v.status.map((row) => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, color: 'var(--gt-text-5)', fontSize: 10 }}>{row.l}</span>
              <div style={{ display: 'flex' }}>
                {row.opts.map((c) => (
                  <div
                    key={c.key}
                    onClick={() =>
                      row.key === 'pt'
                        ? s.setFlt({ pt: c.v as TriState })
                        : s.setStatusFilter(row.key as keyof Filters['st'], c.v)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid ' + c.bd,
                      color: c.clr,
                      background: c.bg,
                      width: 58,
                      height: 40,
                      cursor: 'pointer',
                      marginLeft: -1,
                      position: 'relative',
                      zIndex: Number(c.zi),
                      boxSizing: 'border-box',
                      fontSize: 10,
                    }}
                  >
                    {c.l}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/*
          OWNED BY — the crew as toggles, AND'd (2026-08-27, Marcus: "I should
          be able to filter by owned and toggle on/off specific users").

          The disabled state carries its reason TWICE, on purpose. `data-tip` is
          the house tooltip and it is the desktop half of the answer; the
          `NO LINK` / `CHECKING` badge on the chip is the phone's, because
          `components/Tooltip.tsx` shows nothing for a touch pointer and this
          sheet is the phone's only filter surface. A greyed chip with no stated
          reason reads as a bug.
        */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={LABEL}>OWNED BY</div>
          <div style={{ color: 'var(--gt-faint)', fontFamily: MONO, fontSize: 10 }}>
            {v.owned.none ? v.owned.noneTxt : v.owned.note}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[v.owned.all, ...v.owned.opts].map((o) => (
              <div
                key={o.key}
                data-tip={o.tip}
                onClick={() => {
                  if (o.dis) return;
                  if (o.k === '') s.toggleOwnAll();
                  else s.toggleOwn(o.k);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  border: '1px solid ' + o.bd,
                  color: o.clr,
                  background: o.bg,
                  padding: '8px 10px',
                  // `default`, not `not-allowed`: the chip is not refusing a
                  // legitimate act, it is telling you a fact about that member.
                  cursor: o.dis ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                  fontFamily: MONO,
                  fontSize: 10,
                }}
              >
                {o.k === '' ? null : (
                  <Avatar
                    size={18}
                    fs={7}
                    ini={o.ini}
                    bg={o.hueBg}
                    hasAv={o.hasAv}
                    avBg={o.avBg}
                    style={{ flex: 'none', opacity: o.dis ? 0.4 : 1 }}
                  />
                )}
                {o.l}
                {o.sub ? (
                  <span style={{ color: 'var(--gt-ghost-2)', fontSize: 8.5 }}>· {o.sub}</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/*
          TAGS — the picker, folded in from the retired # TAGS sheet. Still
          Steam's own 446-tag vocabulary, tap to cycle search → exclude → off;
          the preset editorial chips that once closed this sheet are still not
          coming back (docs/DISCOVERY.md §1.4).
        */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            borderTop: '1px solid var(--gt-line)',
            paddingTop: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={LABEL}># BROWSE BY TAG</div>
            <div style={{ color: 'var(--gt-faint)', fontFamily: MONO, fontSize: 10 }}>{v.tags.note}</div>
            {/* A pick means one thing on Discover and another everywhere else
                (`selectors.ts#tagLensOf`), so the sheet says which one is live. */}
            <div style={{ color: 'var(--gt-muted)', fontFamily: MONO, fontSize: 9.5, lineHeight: 1.7 }}>
              {v.tags.appliesTxt}
            </div>
          </div>

          {v.tags.hasPicked ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={LABEL}>PICKED — {v.tags.pickedTitle}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{v.tags.picked.map(tagChip)}</div>
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={LABEL}>POPULAR</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{v.tags.popular.map(tagChip)}</div>
          </div>

          {/*
            THE TAG SEARCH LIFTS ITSELF TO THE TOP OF THE SHEET ON FOCUS
            (2026-09-02, Marcus: *"when you search for tags, your searched tags
            are hidden under the mobile keyboard"*).

            This is the second half of that fix and it is the half that decides
            whether you can READ the answer. `Sheet`'s `useKeyboardInset` shrinks
            the panel to the strip the keyboard leaves, which keeps the whole
            sheet on screen — but this section is the LAST one in the body and
            its results render BELOW the field, so inside that shorter panel the
            field would still sit at the bottom with the matches off the end.
            Scrolling it to the top turns the rest of the strip into room for
            the results.

            `block: 'start'` against a `scrollMarginTop` that clears the sticky
            header, which would otherwise cover the label it just scrolled to.
            The delay lets the keyboard finish animating in — the panel cannot
            be scrolled to a position that does not exist yet, and `kbd` has to
            land before the shorter `maxHeight` gives it that room.
          */}
          <div
            ref={tagSearchRef}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              // Clear of the sticky header, which would otherwise cover the very
              // label this scrolls to. Derived from the header's own constants
              // rather than typed as a number, so the two cannot drift.
              scrollMarginTop: SHEET_HEADER_H + 8,
            }}
          >
            <div style={LABEL}>ALL STEAM TAGS</div>
            <SearchBox
              value={s.tagQ}
              onChange={(e) => s.setTagQ(e.target.value)}
              onFocus={() => {
                setTagFocus(true);
                // After the keyboard has animated in AND the re-render above has
                // laid out the extra room — the scroll position being asked for
                // does not exist until both have happened.
                window.setTimeout(() => {
                  tagSearchRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }, 300);
              }}
              onBlur={() => setTagFocus(false)}
              placeholder={v.tags.searchPlaceholder}
              w="100%"
              pfs={11}
              ifs={12}
              pad="12px 11px"
            />
            {v.tags.hasResults ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{v.tags.results.map(tagChip)}</div>
            ) : null}
            {v.tags.noHit ? (
              <div style={{ color: 'var(--gt-faint)', fontFamily: MONO, fontSize: 10 }}>
                NO STEAM TAG MATCHES THAT
              </div>
            ) : null}
            {v.tags.vocabDown ? (
              <div
                style={{
                  border: '1px dashed var(--gt-border-2)',
                  color: 'var(--gt-muted)',
                  fontFamily: MONO,
                  fontSize: 9,
                  padding: '10px 11px',
                  lineHeight: 1.7,
                }}
              >
                {v.tags.vocabDownTxt}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/*
        sticky footer -----------------------------------------------------

        THE TWO THINGS THAT KEPT IT FROM BEING A FLOOR (2026-09-01, Marcus:
        *"fix the overlaps on the top and bottom bars with the modal content.
        also the bottom bar on the modal does not extend to the bottom of the
        phone"*).

        `zIndex`, for the reason `Sheet`'s header carries one: with none at all
        this row computed `auto`, so every segmented control in the body — all
        of them positioned, all carrying 1 or 2 — painted straight over it. The
        OWNED BY avatars sat on top of DONE.

        And the panel's own `paddingBottom: 28`, now gone. A sticky `bottom: 0`
        resolves against the scrollport, and the padding is INSIDE it, so the
        bar came to rest 28px up from the panel's edge with the last of the
        scrolled content showing through the strip underneath — measured at
        exactly 28px on a 412×915 phone. The safe-area inset replaces it, on the
        footer rather than the panel, so the bar itself reaches the bottom and
        only its padding grows under a gesture bar.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px calc(16px + env(safe-area-inset-bottom, 0px))',
          borderTop: '1px solid var(--gt-line)',
          position: 'sticky',
          bottom: 0,
          background: 'var(--gt-card)',
          zIndex: SHEET_CHROME_Z,
        }}
      >
        {v.hasActive ? (
          <div
            onClick={() => s.clearFlt()}
            style={{
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-5)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10.5,
              padding: '0 14px',
              height: 46,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            CLEAR ALL
          </div>
        ) : null}
        <div style={{ flex: 1 }} />
        <div
          onClick={() => s.toggleFltOpen(false)}
          style={{
            border: '1px solid var(--acc)',
            color: 'var(--acc)',
            background: 'var(--accSoft)',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 10.5,
            padding: '0 26px',
            height: 46,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
            whiteSpace: 'nowrap',
          }}
        >
          DONE · {v.matchN}
        </div>
      </div>
    </Sheet>
  );
}
